export type ScoreBand = 'A' | 'B' | 'C' | 'D' | 'F'

interface ScoreBandStyle {
  textClass: string
  borderClass: string
  ringHex: string
  label: string
  quality: string
}

const BAND_STYLES: Record<ScoreBand, ScoreBandStyle> = {
  A: {
    textClass: 'text-emerald-600',
    borderClass: 'border-emerald-500',
    ringHex: '#10B981',
    label: 'Excellent',
    quality: 'strong match',
  },
  B: {
    textClass: 'text-lime-600',
    borderClass: 'border-lime-500',
    ringHex: '#65A30D',
    label: 'Great',
    quality: 'good match',
  },
  C: {
    textClass: 'text-yellow-600',
    borderClass: 'border-yellow-500',
    ringHex: '#CA8A04',
    label: 'Good',
    quality: 'partial match',
  },
  D: {
    textClass: 'text-orange-600',
    borderClass: 'border-orange-500',
    ringHex: '#EA580C',
    label: 'Weak',
    quality: 'weak match',
  },
  F: {
    textClass: 'text-red-600',
    borderClass: 'border-red-500',
    ringHex: '#DC2626',
    label: 'Weak',
    quality: 'poor match',
  },
}

export function normalizeGlobalToPercent(globalScore?: number | null): number | null {
  if (typeof globalScore !== 'number' || Number.isNaN(globalScore)) return null
  return Math.round(globalScore * 20)
}

export function gradeBandFromGlobal(globalScore?: number | null): ScoreBand | null {
  if (typeof globalScore !== 'number' || Number.isNaN(globalScore)) return null
  if (globalScore >= 4.5) return 'A'
  if (globalScore >= 4.0) return 'B'
  if (globalScore >= 3.5) return 'C'
  if (globalScore >= 3.0) return 'D'
  return 'F'
}

export function gradeBandFromPercent(scorePercent?: number | null): ScoreBand | null {
  if (typeof scorePercent !== 'number' || Number.isNaN(scorePercent)) return null
  if (scorePercent >= 90) return 'A'
  if (scorePercent >= 80) return 'B'
  if (scorePercent >= 70) return 'C'
  if (scorePercent >= 60) return 'D'
  return 'F'
}

export function scoreBandForDisplay(
  globalScore?: number | null,
  scorePercent?: number | null,
): ScoreBand {
  return gradeBandFromGlobal(globalScore) ?? gradeBandFromPercent(scorePercent) ?? 'F'
}

export function scoreBandStyle(band: ScoreBand): ScoreBandStyle {
  return BAND_STYLES[band]
}

export function computeRelativeBoostPercent(args: {
  previousGlobalScore?: number | null
  newGlobalScore?: number | null
  previousScore?: number | null
  newScore?: number | null
  fallbackDeltaPercent?: number | null
}): number | null {
  const { previousGlobalScore, newGlobalScore, previousScore, newScore, fallbackDeltaPercent } =
    args

  if (
    typeof previousGlobalScore === 'number' &&
    previousGlobalScore > 0 &&
    typeof newGlobalScore === 'number'
  ) {
    return Math.round(((newGlobalScore - previousGlobalScore) / previousGlobalScore) * 100)
  }

  if (typeof previousScore === 'number' && previousScore > 0 && typeof newScore === 'number') {
    return Math.round(((newScore - previousScore) / previousScore) * 100)
  }

  if (typeof fallbackDeltaPercent === 'number') return fallbackDeltaPercent
  return null
}
